import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Chip,
} from '@mui/material';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import { ja } from 'date-fns/locale';
import { licenseApi } from '../../api/license';
import { LicenseCreateInput } from '../../types/license';
import { applicationApi } from '../../api/application';
import { userApi } from '../../api/user';

const LicenseCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { register, control, handleSubmit, watch, setValue, formState: { errors } } = useForm<LicenseCreateInput>();
  const [selectedApplication, setSelectedApplication] = useState<any>(null);

  // アプリケーション一覧を取得
  const { data: applications = [], isLoading: isLoadingApps } = useQuery({
    queryKey: ['applications-for-license'],
    queryFn: applicationApi.getApplicationsForLicense,
  });

  // role=USERのユーザー一覧を取得
  const { data: users = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users-for-license'],
    queryFn: userApi.getUsersForLicense,
  });

  const selectedAppId = watch('applicationId');
  const licenseType = watch('type');

  // 選択されたアプリケーションの詳細を更新
  useEffect(() => {
    if (selectedAppId && applications.length > 0) {
      const app = applications.find(app => app.id === selectedAppId);
      setSelectedApplication(app);
    } else {
      setSelectedApplication(null);
    }
  }, [selectedAppId, applications]);

  // ライセンスタイプが永続的の場合、終了日をクリアする
  useEffect(() => {
    if (licenseType === 'perpetual') {
      setValue('endDate', '');
    }
  }, [licenseType, setValue]);

  const createMutation = useMutation({
    mutationFn: licenseApi.createLicense,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      navigate('/licenses');
    },
  });

  const onSubmit = (data: LicenseCreateInput) => {
    createMutation.mutate(data);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        ライセンス新規作成
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.applicationId}>
                <InputLabel>アプリケーション</InputLabel>
                <Controller
                  name="applicationId"
                  control={control}
                  rules={{ required: 'アプリケーションの選択は必須です' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="アプリケーション"
                      disabled={isLoadingApps}
                    >
                      {applications.map((app) => (
                        <MenuItem key={app.id} value={app.id}>
                          <Box>
                            <Typography variant="body1">{app.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              v{app.version} - {app.description || 'No description'}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.applicationId && (
                  <FormHelperText>{errors.applicationId.message}</FormHelperText>
                )}
                {isLoadingApps && (
                  <FormHelperText>アプリケーション一覧を読み込み中...</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.userId}>
                <InputLabel>ユーザー</InputLabel>
                <Controller
                  name="userId"
                  control={control}
                  rules={{ required: 'ユーザーの選択は必須です' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="ユーザー"
                      disabled={isLoadingUsers}
                    >
                      {users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          <Box>
                            <Typography variant="body1">{user.email}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.role} - {user.tenantId}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.userId && (
                  <FormHelperText>{errors.userId.message}</FormHelperText>
                )}
                {isLoadingUsers && (
                  <FormHelperText>ユーザー一覧を読み込み中...</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="ライセンスタイプ"
                {...register('type', { required: 'ライセンスタイプは必須です' })}
                error={!!errors.type}
                helperText={errors.type?.message}
              >
                <MenuItem value="perpetual">永続的</MenuItem>
                <MenuItem value="subscription">サブスクリプション</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="最大アクティベーション数"
                {...register('maxActivations', {
                  required: '最大アクティベーション数は必須です',
                  min: { value: 1, message: '1以上を指定してください' },
                })}
                error={!!errors.maxActivations}
                helperText={errors.maxActivations?.message}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="開始日"
                InputLabelProps={{ shrink: true }}
                {...register('startDate')}
                error={!!errors.startDate}
                helperText={errors.startDate?.message}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="終了日"
                InputLabelProps={{ shrink: true }}
                {...register('endDate')}
                error={!!errors.endDate}
                helperText={
                  licenseType === 'perpetual'
                    ? '永続的ライセンスは無期限です'
                    : errors.endDate?.message
                }
                disabled={licenseType === 'perpetual'}
              />
            </Grid>

            {/* 選択されたアプリケーションの詳細情報 */}
            {selectedApplication && (
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'primary.main' }}
                >
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    選択されたアプリケーション
                  </Typography>
                  <Box display="flex" gap={1} mb={1}>
                    <Chip
                      label={selectedApplication.name}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label={`v${selectedApplication.version}`}
                      color="secondary"
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label={selectedApplication.status}
                      color={selectedApplication.status === 'active' ? 'success' : 'default'}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  {selectedApplication.description && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedApplication.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    アプリケーションID: {selectedApplication.id}
                  </Typography>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => navigate('/licenses')}
                  disabled={createMutation.isPending}
                >
                  キャンセル
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? <CircularProgress size={24} /> : '作成'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default LicenseCreatePage; 